import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from '@/lib/queries';
import { AuthProvider } from '@/lib/auth-context';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { ToastProvider } from '@/components/ui/toast';
import { AppLayout } from '@/components/AppLayout';

// Sign-in is the only route loaded eagerly (it's the first thing unauthed users hit).
import SignInPage from '@/routes/sign-in';

const DashboardPage         = lazy(() => import('@/routes/dashboard'));
const ExperimentsListPage   = lazy(() => import('@/routes/experiments'));
const ExperimentDetailPage  = lazy(() => import('@/routes/experiment-detail'));
const DecisionsListPage     = lazy(() => import('@/routes/decisions'));
const DecisionDetailPage    = lazy(() => import('@/routes/decision-detail'));
const MeetingsListPage      = lazy(() => import('@/routes/meetings'));
const MeetingDetailPage     = lazy(() => import('@/routes/meeting-detail'));
const MetricsListPage       = lazy(() => import('@/routes/metrics'));
const MetricDetailPage      = lazy(() => import('@/routes/metric-detail'));
const MembersPage           = lazy(() => import('@/routes/members'));
const DocsIndexPage         = lazy(() => import('@/routes/docs'));
const DocDetailPage         = lazy(() => import('@/routes/doc-detail'));

function PageFallback() {
  return <div className="page"><div className="card p-8 text-center text-sm text-neutral-500">Loading…</div></div>;
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <AuthProvider>
          <BrowserRouter>
            <Suspense fallback={<PageFallback />}>
              <Routes>
                <Route path="/sign-in" element={<SignInPage />} />
                <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
                  <Route path="/" element={<DashboardPage />} />
                  <Route path="/experiments" element={<ExperimentsListPage />} />
                  <Route path="/experiments/:id" element={<ExperimentDetailPage />} />
                  <Route path="/decisions" element={<DecisionsListPage />} />
                  <Route path="/decisions/:id" element={<DecisionDetailPage />} />
                  <Route path="/meetings" element={<MeetingsListPage />} />
                  <Route path="/meetings/:id" element={<MeetingDetailPage />} />
                  <Route path="/metrics" element={<MetricsListPage />} />
                  <Route path="/metrics/:id" element={<MetricDetailPage />} />
                  <Route path="/members" element={<MembersPage />} />
                  <Route path="/docs" element={<DocsIndexPage />} />
                  <Route path="/docs/:slug" element={<DocDetailPage />} />
                </Route>
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </AuthProvider>
      </ToastProvider>
    </QueryClientProvider>
  );
}
